/**
 * Centralized Price Service for DeepTrade Frontend
 * Handles real-time price updates via Server-Sent Events (SSE)
 */

import { API_BASE_URL } from '../config';
import { authenticatedFetch, getFreshAccessToken } from '@/utils/authFetch';

export interface PriceData {
    symbol: string;
    price: number;
    timestamp: number;
    last_updated: string;
    price_source?: string;
    change_24h?: number;
    change_percent_24h?: number;
    high_24h?: number;
    low_24h?: number;
    volume_24h?: number;
    quote_volume_24h?: number;
    last_price?: number;
    mark_price?: number;
    markPrice?: number;
    is_stale?: boolean;
    stale_age_ms?: number;
    [key: string]: unknown;
}

export interface PriceUpdate {
    type: 'connected' | 'price_update' | 'heartbeat' | 'error';
    symbol?: string;
    data?: PriceData;
    current_prices?: { [symbol: string]: PriceData };
    timestamp: string;
    message?: string;
}

export type PriceUpdateCallback = (
    symbol: string,
    priceData: PriceData,
    previousPrice?: number,
) => void;

export class PriceService {
    private eventSource: EventSource | null = null;
    private subscribers: Map<string, PriceUpdateCallback[]> = new Map();
    private currentPrices: Map<string, PriceData> = new Map();
    private recoveryPollIntervals: Map<string, number> = new Map();
    private pollingSymbols: Set<string> = new Set();
    private isConnected = false;
    private isConnecting = false;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 1000; // Start with 1 second
    private recoveryPollMs = 10000;

    constructor() {}

    private toFiniteNumber(value: unknown): number | undefined {
        if (value === null || value === undefined) {
            return undefined;
        }

        const num = Number(value);
        return Number.isFinite(num) ? num : undefined;
    }

    private normalizePriceData(raw: any): PriceData {
        const symbol = typeof raw?.symbol === 'string' ? raw.symbol.toUpperCase() : 'UNKNOWN';
        const mark = this.toFiniteNumber(raw?.mark_price ?? raw?.markPrice);
        const spotPrice = this.toFiniteNumber(raw?.price);
        const resolvedPrice = mark ?? spotPrice ?? 0;
        const timestamp = this.toFiniteNumber(raw?.timestamp) ?? Date.now();

        const change = this.toFiniteNumber(raw?.change_24h ?? raw?.change24h ?? raw?.price_change);
        const changePercent = this.toFiniteNumber(
            raw?.change_percent_24h ?? raw?.changePercent24h ?? raw?.price_change_percent,
        );
        const high = this.toFiniteNumber(raw?.high_24h ?? raw?.highPrice ?? raw?.high_price);
        const low = this.toFiniteNumber(raw?.low_24h ?? raw?.lowPrice ?? raw?.low_price);
        const volume = this.toFiniteNumber(raw?.volume_24h ?? raw?.volume);
        const quoteVolume = this.toFiniteNumber(
            raw?.quote_volume_24h ?? raw?.quoteVolume ?? raw?.quote_volume,
        );
        const lastPrice = this.toFiniteNumber(raw?.last_price ?? raw?.lastPrice);

        const priceSource =
            typeof raw?.price_source === 'string'
                ? raw.price_source
                : typeof raw?.priceSource === 'string'
                  ? raw.priceSource
                  : undefined;

        const normalized: PriceData = {
            ...raw,
            symbol,
            price: resolvedPrice,
            timestamp,
            last_updated:
                typeof raw?.last_updated === 'string'
                    ? raw.last_updated
                    : new Date(timestamp).toISOString(),
            price_source: priceSource,
            change_24h: change,
            change_percent_24h: changePercent,
            high_24h: high,
            low_24h: low,
            volume_24h: volume,
            quote_volume_24h: quoteVolume,
            last_price: lastPrice,
            mark_price: mark ?? resolvedPrice,
            markPrice: mark ?? resolvedPrice,
        };

        return normalized;
    }

    private upsertPrice(symbol: string, raw: any): PriceData {
        const upperSymbol = symbol.toUpperCase();
        const normalized = this.normalizePriceData({ ...raw, symbol: upperSymbol });
        const previousEntry = this.currentPrices.get(upperSymbol);
        const previousPrice =
            previousEntry?.mark_price ?? previousEntry?.markPrice ?? previousEntry?.price;

        this.currentPrices.set(upperSymbol, normalized);
        this.notifySubscribers(upperSymbol, normalized, this.toFiniteNumber(previousPrice));
        return normalized;
    }

    private recoveryPollFailStreak = 0;

    private async pollLatestPrice(symbol: string): Promise<void> {
        const upperSymbol = symbol.toUpperCase();
        if (this.pollingSymbols.has(upperSymbol)) {
            return;
        }
        // Recovery poll runs only while SSE is unhealthy; never poll hidden tabs.
        if (typeof document !== 'undefined' && document.hidden) {
            return;
        }
        // Back off after consecutive failures (e.g. 503 with no shared data yet)
        // so a missing shelf doesn't spam the console or the backend.
        if (this.recoveryPollFailStreak >= 3) {
            this.recoveryPollFailStreak = 0;
            return;
        }

        this.pollingSymbols.add(upperSymbol);

        try {
            const response = await authenticatedFetch(
                `${API_BASE_URL}/api/trading/live-price/${upperSymbol}`,
                {
                    method: 'GET',
                },
            );

            if (!response.ok) {
                this.recoveryPollFailStreak += 1;
                return;
            }

            const raw = await response.json();
            this.recoveryPollFailStreak = 0;
            this.hydratePrice(upperSymbol, raw);
        } catch (error) {
            this.recoveryPollFailStreak += 1;
            console.error(`Failed to poll latest price for ${upperSymbol}:`, error);
        } finally {
            this.pollingSymbols.delete(upperSymbol);
        }
    }

    private startRecoveryPolling(symbol: string): void {
        const upperSymbol = symbol.toUpperCase();
        if (this.recoveryPollIntervals.has(upperSymbol)) {
            return;
        }

        void this.pollLatestPrice(upperSymbol);
        const intervalId = window.setInterval(() => {
            void this.pollLatestPrice(upperSymbol);
        }, this.recoveryPollMs);

        this.recoveryPollIntervals.set(upperSymbol, intervalId);
    }

    private stopRecoveryPolling(symbol: string): void {
        const upperSymbol = symbol.toUpperCase();
        const intervalId = this.recoveryPollIntervals.get(upperSymbol);
        if (intervalId !== undefined) {
            window.clearInterval(intervalId);
            this.recoveryPollIntervals.delete(upperSymbol);
        }
        this.pollingSymbols.delete(upperSymbol);
    }

    private syncRecoveryPollingForSubscribers(): void {
        const trackedSymbols = new Set<string>([
            ...this.subscribers.keys(),
            ...this.recoveryPollIntervals.keys(),
        ]);

        trackedSymbols.forEach(symbol => {
            const upperSymbol = symbol.toUpperCase();

            const hasSubscribers = (this.subscribers.get(upperSymbol)?.length ?? 0) > 0;
            if (!hasSubscribers) {
                this.stopRecoveryPolling(upperSymbol);
                return;
            }

            this.startRecoveryPolling(upperSymbol);
        });
    }

    /**
     * Start the price streaming connection
     */
    connect(): void {
        if (this.isConnected || this.isConnecting || this.eventSource) {
            return;
        }

        this.isConnecting = true;
        void this.openConnection();
    }

    private async openConnection(): Promise<void> {
        try {
            const token = await getFreshAccessToken();
            if (!token) {
                console.error('No auth token found for price streaming');
                this.isConnecting = false;
                return;
            }

            // Close existing connection if any
            this.disconnect();

            this.eventSource = new EventSource(
                `${API_BASE_URL}/api/realtime/price-stream?token=${token}`,
            );

            this.eventSource.onopen = () => {
                console.log('Price streaming connection established');
                this.isConnected = true;
                this.isConnecting = false;
                this.reconnectAttempts = 0;
                this.reconnectDelay = 1000;
                this.syncRecoveryPollingForSubscribers();
            };

            this.eventSource.onmessage = event => {
                try {
                    const update: PriceUpdate = JSON.parse(event.data);
                    this.handlePriceUpdate(update);
                } catch (error) {
                    console.error('Error parsing price update:', error);
                }
            };

            this.eventSource.onerror = error => {
                console.error('Price streaming error:', error);
                this.isConnected = false;
                this.isConnecting = false;
                this.disconnect();
                this.syncRecoveryPollingForSubscribers();

                // Attempt to reconnect with exponential backoff
                if (this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.reconnectAttempts++;
                    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

                    console.log(
                        `Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
                    );

                    setTimeout(() => {
                        this.connect();
                    }, delay);
                } else {
                    console.error('Max reconnection attempts reached. Price streaming disabled.');
                }
            };
        } catch (error) {
            console.error('Error establishing price streaming connection:', error);
            this.isConnecting = false;
        }
    }

    /**
     * Disconnect from price streaming
     */
    disconnect(): void {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
        this.isConnected = false;
        this.isConnecting = false;
        this.syncRecoveryPollingForSubscribers();
    }

    /**
     * Handle incoming price updates
     */
    private handlePriceUpdate(update: PriceUpdate): void {
        switch (update.type) {
            case 'connected':
                console.log('Price streaming connected');
                this.isConnected = true;
                this.isConnecting = false;
                this.syncRecoveryPollingForSubscribers();
                // Load initial prices
                if (update.current_prices) {
                    Object.entries(update.current_prices).forEach(([symbol, priceData]) => {
                        this.upsertPrice(symbol, priceData);
                    });
                }
                break;

            case 'price_update':
                if (update.symbol && update.data) {
                    this.upsertPrice(update.symbol, update.data);
                }
                break;

            case 'heartbeat':
                // Keep connection alive
                break;

            case 'error':
                console.error('Price streaming error:', update.message);
                break;

            default:
                console.warn('Unknown price update type:', update.type);
        }
    }

    /**
     * Notify subscribers of price updates
     */
    private notifySubscribers(symbol: string, priceData: PriceData, previousPrice?: number): void {
        const callbacks = this.subscribers.get(symbol) || [];
        callbacks.forEach(callback => {
            try {
                callback(symbol, priceData, previousPrice);
            } catch (error) {
                console.error('Error in price update callback:', error);
            }
        });
    }

    /**
     * Subscribe to price updates for a specific symbol
     */
    subscribe(symbol: string, callback: PriceUpdateCallback): () => void {
        const upperSymbol = symbol.toUpperCase();

        if (!this.subscribers.has(upperSymbol)) {
            this.subscribers.set(upperSymbol, []);
        }

        this.subscribers.get(upperSymbol)!.push(callback);

        // Start connection if not already connected
        if (!this.isConnected) {
            this.connect();
        }

        // Keep BTC refreshed only while SSE is unavailable or unhealthy.
        if (upperSymbol === 'BTCUSDT') {
            this.syncRecoveryPollingForSubscribers();
        }

        // Send current price if available
        const currentPrice = this.currentPrices.get(upperSymbol);
        if (currentPrice) {
            setTimeout(() => callback(upperSymbol, currentPrice), 0);
        }

        // Return unsubscribe function
        return () => {
            const callbacks = this.subscribers.get(upperSymbol);
            if (callbacks) {
                const index = callbacks.indexOf(callback);
                if (index > -1) {
                    callbacks.splice(index, 1);
                }

                // Remove symbol if no more subscribers
                if (callbacks.length === 0) {
                    this.subscribers.delete(upperSymbol);
                    this.stopRecoveryPolling(upperSymbol);
                }
            }

            this.syncRecoveryPollingForSubscribers();

            // Disconnect if no more subscribers
            if (this.subscribers.size === 0) {
                Array.from(this.recoveryPollIntervals.keys()).forEach(trackedSymbol => {
                    this.stopRecoveryPolling(trackedSymbol);
                });
                this.disconnect();
            }
        };
    }

    /**
     * Get current price for a symbol
     */
    getCurrentPrice(symbol: string): PriceData | null {
        return this.currentPrices.get(symbol.toUpperCase()) || null;
    }

    hydratePrice(symbol: string, rawPriceData: unknown): PriceData | null {
        if (!symbol || rawPriceData === null || rawPriceData === undefined) {
            return null;
        }

        try {
            return this.upsertPrice(symbol, rawPriceData);
        } catch (error) {
            console.error('Failed to hydrate price data:', error);
            return null;
        }
    }

    /**
     * Get all current prices
     */
    getAllPrices(): { [symbol: string]: PriceData } {
        const prices: { [symbol: string]: PriceData } = {};
        this.currentPrices.forEach((priceData, symbol) => {
            prices[symbol] = priceData;
        });
        return prices;
    }

    /**
     * Get connection status
     */
    getConnectionStatus(): boolean {
        return this.isConnected;
    }

    /**
     * Force reconnection
     */
    reconnect(): void {
        this.disconnect();
        this.reconnectAttempts = 0;
        this.connect();
    }
}

// Global price service instance
export const priceService = new PriceService();
